/**
 * Real Safe Asset Service
 *
 * Fetches actual asset data from Safe services and blockchain APIs
 * Replaces all mock/placeholder asset data
 */

import type { SafeInfo } from '@safe-global/safe-apps-sdk';
import { ethers } from 'ethers';

export interface SafeAsset {
  id: string;
  type: 'ETH' | 'ERC20' | 'ERC721';
  name: string;
  symbol: string;
  balance: string;
  decimals?: number;
  tokenAddress?: string;
  tokenId?: string;
  imageUrl?: string;
  usdValue?: string;
  lastUpdated: number;
}

export interface AssetBalance {
  tokenAddress: string | null; // null for ETH
  token: {
    name: string;
    symbol: string;
    decimals: number;
    logoUri?: string;
  } | null;
  balance: string;
  fiatBalance?: string;
  fiatConversion?: string;
}

export interface NFTAsset {
  address: string;
  tokenName: string;
  tokenSymbol: string;
  logoUri?: string;
  id: string;
  uri?: string;
  name?: string;
  description?: string;
  imageUri?: string;
  metadata?: any;
}

export class SafeAssetService {
  private readonly SAFE_TRANSACTION_SERVICE_URLS: Record<number, string> = {
    1: 'https://safe-transaction-mainnet.safe.global',
    5: 'https://safe-transaction-goerli.safe.global',
    10: 'https://safe-transaction-optimism.safe.global',
    100: 'https://safe-transaction-gnosis-chain.safe.global',
    137: 'https://safe-transaction-polygon.safe.global',
    42161: 'https://safe-transaction-arbitrum.safe.global',
    11155111: 'https://safe-transaction-sepolia.safe.global',
  };

  private readonly RPC_URLS: Record<number, string[]> = {
    1: [
      'https://eth.llamarpc.com',
      'https://ethereum-rpc.publicnode.com',
      'https://1rpc.io/eth',
      'https://rpc.ankr.com/eth'
    ],
    5: [
      'https://ethereum-goerli-rpc.publicnode.com',
      'https://rpc.ankr.com/eth_goerli'
    ],
    10: [
      'https://optimism-rpc.publicnode.com',
      'https://1rpc.io/op',
      'https://rpc.ankr.com/optimism'
    ],
    100: [
      'https://rpc.gnosischain.com',
      'https://gnosis-rpc.publicnode.com'
    ],
    137: [
      'https://polygon-rpc.com',
      'https://1rpc.io/matic',
      'https://rpc.ankr.com/polygon',
      'https://polygon-bor-rpc.publicnode.com'
    ],
    42161: [
      'https://1rpc.io/arb',
      'https://arbitrum.meowrpc.com',
      'https://arbitrum.drpc.org',
      'https://rpc.ankr.com/arbitrum'
    ],
    11155111: [
      'https://sepolia.infura.io/v3/9aa3d95b3bc440fa88ea12eaa4456161', // Infura public endpoint
      'https://ethereum-sepolia-rpc.publicnode.com',
      'https://rpc2.sepolia.org',
      'https://1rpc.io/sepolia',
      'https://rpc.ankr.com/eth_sepolia',
      'https://sepolia.gateway.tenderly.co'
    ],
  };

  private provider: ethers.Provider | null = null;
  private safeServiceUrl: string | null = null;
  private rpcUrls: string[] = [];
  private currentRpcIndex = 0;

  constructor(private chainId: number, safeProvider?: ethers.Provider) {
    this.safeServiceUrl = this.SAFE_TRANSACTION_SERVICE_URLS[chainId] || null;
    this.rpcUrls = this.RPC_URLS[chainId] || [];

    if (safeProvider) {
      // Use Safe's provided web3 provider - no CSP issues
      console.log('Using Safe-provided web3 provider');
      this.provider = safeProvider;
    } else if (this.rpcUrls.length > 0) {
      console.log('Safe provider not available, attempting direct RPC (may fail due to CSP)');
      this.initializeProvider();
      // Verify RPC on initialization
      this.verifyRPCIntegrity();
    }
  }

  /**
   * Verify RPC integrity by checking chain ID and comparing block data
   */
  private async verifyRPCIntegrity(): Promise<void> {
    if (!this.provider) return;

    try {
      // Verify chain ID matches expected
      const network = await this.provider.getNetwork();
      if (Number(network.chainId) !== this.chainId) {
        console.warn(`RPC chain ID mismatch: expected ${this.chainId}, got ${network.chainId}`);
        await this.switchToNextRpc();
        return;
      }

      // Get latest block to ensure RPC is responsive
      const latestBlock = await this.provider.getBlockNumber();
      console.log(`RPC verification passed - Chain ID: ${this.chainId}, Latest Block: ${latestBlock}`);
    } catch (error) {
      console.error('RPC verification failed:', error);
      await this.switchToNextRpc();
    }
  }

  private initializeProvider() {
    if (this.rpcUrls.length === 0) return;

    const rpcUrl = this.rpcUrls[this.currentRpcIndex];
    console.log(`Initializing RPC provider with: ${rpcUrl}`);

    this.provider = new ethers.JsonRpcProvider(rpcUrl, this.chainId, {
      staticNetwork: true,
      pollingInterval: 8000, // 8 seconds
    });

    // Set shorter timeout to fail fast on slow RPCs (only for JsonRpcProvider)
    if (this.provider && 'connection' in this.provider && typeof (this.provider as any)._getConnection === 'function') {
      try {
        const connection = (this.provider as any)._getConnection();
        if (connection) {
          connection.timeout = 6000; // 6 second timeout
        }
      } catch (error) {
        // Ignore timeout setting errors for non-JsonRpcProvider instances
        console.log('Could not set timeout for this provider type');
      }
    }
  }

  private async switchToNextRpc(): Promise<boolean> {
    this.currentRpcIndex++;
    if (this.currentRpcIndex >= this.rpcUrls.length) {
      this.currentRpcIndex = 0;
      return false; // We've tried all RPCs
    }

    console.log(`Switching to next RPC endpoint: ${this.rpcUrls[this.currentRpcIndex]}`);
    this.initializeProvider();
    return true;
  }

  private async executeWithRetry<T>(operation: () => Promise<T>, operationName: string): Promise<T> {
    const maxRetries = this.rpcUrls.length;
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const result = await Promise.race([
          operation(),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('RPC timeout')), 5000) // Reduced to 5s
          )
        ]);

        // If successful and we had switched RPCs, log the working endpoint
        if (attempt > 0) {
          console.log(`${operationName} successful with RPC: ${this.rpcUrls[this.currentRpcIndex]}`);
        }

        return result;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        console.error(`${operationName} failed with RPC ${this.rpcUrls[this.currentRpcIndex]}:`, lastError.message);

        // Don't switch RPC on the last attempt
        if (attempt < maxRetries - 1) {
          const switched = await this.switchToNextRpc();
          if (!switched) break; // We've tried all RPCs
        }
      }
    }

    throw new Error(`${operationName} failed with all available RPC endpoints. Last error: ${lastError?.message}`);
  }

  /**
   * Fetch all assets for a Safe address with cross-provider verification
   */
  async fetchSafeAssets(safeAddress: string): Promise<SafeAsset[]> {
    const assets: SafeAsset[] = [];

    try {
      // Perform initial integrity check
      await this.verifyRPCIntegrity();

      // Fetch ETH balance
      const ethBalance = await this.fetchETHBalance(safeAddress);
      if (ethBalance) {
        assets.push(ethBalance);
      }

      // Fetch ERC20 tokens
      const erc20Tokens = await this.fetchERC20Balances(safeAddress);
      assets.push(...erc20Tokens);

      // Fetch ERC721 NFTs
      const nfts = await this.fetchNFTs(safeAddress);
      assets.push(...nfts);

      console.log(`Successfully fetched ${assets.length} assets using RPC: ${this.getCurrentRpcUrl()}`);
      return assets;
    } catch (error) {
      console.error('Failed to fetch Safe assets:', error);
      throw new Error(`Failed to fetch assets for Safe ${safeAddress}: ${error}`);
    }
  }

  /**
   * Fetch ETH balance for Safe
   */
  private async fetchETHBalance(safeAddress: string): Promise<SafeAsset | null> {
    if (!this.provider) return null;

    try {
      const balance = await this.executeWithRetry(
        () => this.provider!.getBalance(safeAddress),
        'ETH balance fetch'
      );
      const ethAmount = ethers.formatEther(balance);

      return {
        id: `eth-${safeAddress}`,
        type: 'ETH',
        name: 'Ethereum',
        symbol: 'ETH',
        balance: ethAmount,
        decimals: 18,
        lastUpdated: Date.now()
      };
    } catch (error) {
      console.error('Failed to fetch ETH balance:', error);
      return null;
    }
  }

  /**
   * Fetch ERC20 token balances from Safe Transaction Service
   */
  private async fetchERC20Balances(safeAddress: string): Promise<SafeAsset[]> {
    if (!this.safeServiceUrl) return [];

    try {
      const response = await fetch(`${this.safeServiceUrl}/api/v1/safes/${safeAddress}/balances/`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const assets: SafeAsset[] = [];

      for (const balance of data as AssetBalance[]) {
        // Skip ETH (already handled)
        if (!balance.tokenAddress || !balance.token) continue;

        // Only include tokens with non-zero balance
        if (parseFloat(balance.balance) > 0) {
          const tokenBalance = ethers.formatUnits(balance.balance, balance.token.decimals);

          assets.push({
            id: `erc20-${balance.tokenAddress}`,
            type: 'ERC20',
            name: balance.token.name,
            symbol: balance.token.symbol,
            balance: tokenBalance,
            decimals: balance.token.decimals,
            tokenAddress: balance.tokenAddress,
            imageUrl: balance.token.logoUri,
            usdValue: balance.fiatBalance,
            lastUpdated: Date.now()
          });
        }
      }

      return assets;
    } catch (error) {
      console.error('Failed to fetch ERC20 balances:', error);
      return [];
    }
  }

  /**
   * Fetch ERC721 NFTs from Safe Transaction Service
   */
  private async fetchNFTs(safeAddress: string): Promise<SafeAsset[]> {
    if (!this.safeServiceUrl) return [];

    try {
      const response = await fetch(`${this.safeServiceUrl}/api/v2/safes/${safeAddress}/collectibles/`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const assets: SafeAsset[] = [];

      for (const nft of data.results as NFTAsset[]) {
        assets.push({
          id: `nft-${nft.address}-${nft.id}`,
          type: 'ERC721',
          name: nft.name || `${nft.tokenName} #${nft.id}`,
          symbol: nft.tokenSymbol,
          balance: '1',
          tokenAddress: nft.address,
          tokenId: nft.id,
          imageUrl: nft.imageUri || nft.logoUri,
          lastUpdated: Date.now()
        });
      }

      return assets;
    } catch (error) {
      console.error('Failed to fetch NFTs:', error);
      return [];
    }
  }

  /**
   * Get asset details for a specific token address
   */
  async getAssetDetails(tokenAddress: string, tokenId?: string): Promise<SafeAsset | null> {
    if (!this.provider) return null;

    try {
      if (tokenId) {
        // ERC721 token
        return await this.getERC721Details(tokenAddress, tokenId);
      } else {
        // ERC20 token
        return await this.getERC20Details(tokenAddress);
      }
    } catch (error) {
      console.error('Failed to get asset details:', error);
      return null;
    }
  }

  private async getERC20Details(tokenAddress: string): Promise<SafeAsset | null> {
    if (!this.provider) return null;

    try {
      // ERC20 ABI for basic info
      const erc20ABI = [
        'function name() view returns (string)',
        'function symbol() view returns (string)',
        'function decimals() view returns (uint8)'
      ];

      const contract = new ethers.Contract(tokenAddress, erc20ABI, this.provider);

      const [name, symbol, decimals] = await this.executeWithRetry(
        () => Promise.all([
          contract.name(),
          contract.symbol(),
          contract.decimals()
        ]),
        'ERC20 details fetch'
      );

      return {
        id: `erc20-${tokenAddress}`,
        type: 'ERC20',
        name: name,
        symbol: symbol,
        balance: '0',
        decimals: Number(decimals),
        tokenAddress: tokenAddress,
        lastUpdated: Date.now()
      };
    } catch (error) {
      console.error('Failed to get ERC20 details:', error);
      return null;
    }
  }

  private async getERC721Details(tokenAddress: string, tokenId: string): Promise<SafeAsset | null> {
    if (!this.provider) return null;

    try {
      // ERC721 ABI for basic info
      const erc721ABI = [
        'function name() view returns (string)',
        'function symbol() view returns (string)',
        'function tokenURI(uint256 tokenId) view returns (string)'
      ];

      const contract = new ethers.Contract(tokenAddress, erc721ABI, this.provider);

      const [name, symbol, tokenURI] = await this.executeWithRetry(
        () => Promise.all([
          contract.name(),
          contract.symbol(),
          contract.tokenURI(tokenId).catch(() => '')
        ]),
        'ERC721 details fetch'
      );

      // Try to fetch metadata from tokenURI
      let metadata = null;
      let imageUrl = undefined;

      if (tokenURI) {
        try {
          const metadataResponse = await fetch(tokenURI);
          if (metadataResponse.ok) {
            metadata = await metadataResponse.json();
            imageUrl = metadata.image || metadata.image_url;
          }
        } catch (error) {
          console.log('Could not fetch NFT metadata:', error);
        }
      }

      return {
        id: `nft-${tokenAddress}-${tokenId}`,
        type: 'ERC721',
        name: metadata?.name || `${name} #${tokenId}`,
        symbol: symbol,
        balance: '1',
        tokenAddress: tokenAddress,
        tokenId: tokenId,
        imageUrl: imageUrl,
        lastUpdated: Date.now()
      };
    } catch (error) {
      console.error('Failed to get ERC721 details:', error);
      return null;
    }
  }

  /**
   * Check if this chain/network is supported
   */
  isSupported(): boolean {
    return this.safeServiceUrl !== null && this.rpcUrls.length > 0;
  }

  /**
   * Get current RPC endpoint being used
   */
  getCurrentRpcUrl(): string | null {
    return this.rpcUrls.length > 0 ? this.rpcUrls[this.currentRpcIndex] : null;
  }

  /**
   * Get all available RPC endpoints for this network
   */
  getAvailableRpcUrls(): string[] {
    return [...this.rpcUrls];
  }

  /**
   * Get the network name for display
   */
  getNetworkName(): string {
    const names: Record<number, string> = {
      1: 'Ethereum Mainnet',
      5: 'Goerli Testnet',
      10: 'Optimism',
      100: 'Gnosis Chain',
      137: 'Polygon',
      42161: 'Arbitrum One',
      11155111: 'Sepolia Testnet',
    };
    return names[this.chainId] || `Chain ID: ${this.chainId}`;
  }
}

/**
 * Create SafeAssetService instance for a given Safe
 */
export function createSafeAssetService(safe: SafeInfo, provider?: ethers.Provider): SafeAssetService {
  return new SafeAssetService(safe.chainId, provider);
}